import type {
  CellCategory,
  Component,
  ComponentDirection,
  ComponentRelation,
  ComponentRole,
  ComponentType,
  RegionFeatures,
} from "./types";
import { addressFromRC } from "../../rules/audit/addressing";

/**
 * Bottom-up component detection inside a region.
 *
 * 1. For every non-empty cell, compute the length of its maximal row-run
 *    (cells to its left + right with the same category) and column-run
 *    (cells above + below).
 * 2. Assign each cell a direction: row if its row-run is longer, column if
 *    its column-run is longer (column wins on a tie because most tabular
 *    layouts are column-oriented). Isolated cells (both runs length 1) are
 *    direction='single'.
 * 3. Walk the grid and coalesce same-direction, same-category cells into
 *    `Component` records.
 * 4. Run a pairing pass that walks the assembled components and assigns
 *    each one a structural role (header / data-column / total-row / etc.)
 *    based on adjacency and content.
 *
 * All component bounds are sheet-absolute. The function returns components
 * sorted in a stable order: by direction (column, row, single) then by
 * top-left position.
 */
export function detectAndPairComponents(features: RegionFeatures): Component[] {
  const rowCount = features.rowCount;
  const colCount = features.colCount;
  if (rowCount === 0 || colCount === 0) return [];

  // ---------- Step 1: per-cell category grid ----------
  const cat: CellCategory[][] = [];
  for (let r = 0; r < rowCount; r++) {
    const row: CellCategory[] = new Array(colCount);
    for (let c = 0; c < colCount; c++) {
      row[c] = features.rows[r].cells[c].category;
    }
    cat.push(row);
  }

  // ---------- Step 2: row-run lengths ----------
  const rowRunLen: number[][] = [];
  for (let r = 0; r < rowCount; r++) {
    const row = new Array<number>(colCount).fill(0);
    let c = 0;
    while (c < colCount) {
      if (cat[r][c] === "empty") {
        c++;
        continue;
      }
      const category = cat[r][c];
      let end = c;
      while (end + 1 < colCount && cat[r][end + 1] === category) end++;
      const length = end - c + 1;
      for (let k = c; k <= end; k++) row[k] = length;
      c = end + 1;
    }
    rowRunLen.push(row);
  }

  // ---------- Step 3: column-run lengths ----------
  const colRunLen: number[][] = [];
  for (let r = 0; r < rowCount; r++) colRunLen.push(new Array<number>(colCount).fill(0));
  for (let c = 0; c < colCount; c++) {
    let r = 0;
    while (r < rowCount) {
      if (cat[r][c] === "empty") {
        r++;
        continue;
      }
      const category = cat[r][c];
      let end = r;
      while (end + 1 < rowCount && cat[end + 1][c] === category) end++;
      const length = end - r + 1;
      for (let k = r; k <= end; k++) colRunLen[k][c] = length;
      r = end + 1;
    }
  }

  // ---------- Step 4: assign direction per cell ----------
  // 0 = unset (empty); 1 = row; 2 = column; 3 = single.
  const direction: number[][] = [];
  for (let r = 0; r < rowCount; r++) {
    const row = new Array<number>(colCount).fill(0);
    for (let c = 0; c < colCount; c++) {
      if (cat[r][c] === "empty") continue;
      const rl = rowRunLen[r][c];
      const cl = colRunLen[r][c];
      if (rl <= 1 && cl <= 1) {
        row[c] = 3; // single
      } else if (rl > cl) {
        row[c] = 1; // row
      } else {
        row[c] = 2; // column (default on tie)
      }
    }
    direction.push(row);
  }

  // ---------- Step 5: coalesce into components ----------
  const components: Component[] = [];
  let nextId = 0;
  const mkId = (): string => `c${nextId++}`;

  // Column-direction components
  for (let c = 0; c < colCount; c++) {
    let r = 0;
    while (r < rowCount) {
      if (direction[r][c] !== 2) {
        r++;
        continue;
      }
      const category = cat[r][c];
      let end = r;
      while (end + 1 < rowCount && direction[end + 1][c] === 2 && cat[end + 1][c] === category) {
        end++;
      }
      components.push(buildComponent(features, "column", category, r, c, end, c, mkId()));
      r = end + 1;
    }
  }

  // Row-direction components
  for (let r = 0; r < rowCount; r++) {
    let c = 0;
    while (c < colCount) {
      if (direction[r][c] !== 1) {
        c++;
        continue;
      }
      const category = cat[r][c];
      let end = c;
      while (end + 1 < colCount && direction[r][end + 1] === 1 && cat[r][end + 1] === category) {
        end++;
      }
      components.push(buildComponent(features, "row", category, r, c, r, end, mkId()));
      c = end + 1;
    }
  }

  // Single-cell components
  for (let r = 0; r < rowCount; r++) {
    for (let c = 0; c < colCount; c++) {
      if (direction[r][c] !== 3) continue;
      components.push(buildComponent(features, "single", cat[r][c], r, c, r, c, mkId()));
    }
  }

  // Stable sort: column components first, then rows, then singles; each
  // group sorted top-left.
  components.sort((a, b) => {
    const dirOrder = (d: ComponentDirection): number => (d === "column" ? 0 : d === "row" ? 1 : 2);
    const da = dirOrder(a.direction);
    const db = dirOrder(b.direction);
    if (da !== db) return da - db;
    if (a.minRow !== b.minRow) return a.minRow - b.minRow;
    return a.minCol - b.minCol;
  });

  // ---------- Step 6: pairing pass — assign roles ----------
  pairComponents(features, components);

  return components;
}

function buildComponent(
  features: RegionFeatures,
  direction: ComponentDirection,
  category: CellCategory,
  /** Region-relative coords (0-based). */
  rMin: number,
  cMin: number,
  rMax: number,
  cMax: number,
  id: string
): Component {
  const length =
    direction === "row" ? cMax - cMin + 1 : direction === "column" ? rMax - rMin + 1 : 1;
  const absMinRow = features.minRow + rMin;
  const absMinCol = features.minCol + cMin;
  const absMaxRow = features.minRow + rMax;
  const absMaxCol = features.minCol + cMax;

  // Gather cells (non-empty only since we coalesced by category)
  let textC = 0;
  let numC = 0;
  let dateC = 0;
  let formC = 0;
  let boolC = 0;
  let errC = 0;
  let nonEmpty = 0;
  let firstFormula = "";
  let uniformFormula = true;
  let hasFormula = false;
  const distinctValues = new Set<string>();

  for (let r = rMin; r <= rMax; r++) {
    for (let c = cMin; c <= cMax; c++) {
      const cell = features.rows[r].cells[c];
      if (cell.category === "empty") continue;
      nonEmpty++;
      switch (cell.category) {
        case "text":
          textC++;
          break;
        case "numeric":
          numC++;
          break;
        case "date":
          dateC++;
          break;
        case "formula":
          formC++;
          hasFormula = true;
          if (!firstFormula) firstFormula = cell.formula;
          else if (cell.formula !== firstFormula) uniformFormula = false;
          break;
        case "boolean":
          boolC++;
          break;
        case "error":
          errC++;
          break;
      }
      // Build a stable distinct-value key
      const v = cell.value;
      const key = v === null || v === undefined ? "" : String(v);
      distinctValues.add(key);
    }
  }

  const total = nonEmpty || 1;
  const dominantType: ComponentType =
    category === "empty" || category === "error" ? "mixed" : (category as ComponentType);
  // Since runs are same-category, homogeneity is 1 by construction. Compute
  // it anyway in case future variants relax that.
  const dominantCount = Math.max(textC, numC, dateC, formC, boolC, errC);
  const typeHomogeneity = dominantCount / total;
  const uniqueness = distinctValues.size / total;
  // hasFormula stays false if no formula cells. uniformFormula only meaningful
  // when hasFormula true — keep it true when no formulas (vacuously).
  if (!hasFormula) uniformFormula = true;

  return {
    id,
    direction,
    minRow: absMinRow,
    minCol: absMinCol,
    maxRow: absMaxRow,
    maxCol: absMaxCol,
    length,
    cellCount: nonEmpty,
    dominantType,
    typeHomogeneity,
    uniqueness,
    hasFormula,
    uniformFormula,
    address:
      absMinRow === absMaxRow && absMinCol === absMaxCol
        ? addressFromRC(absMinRow, absMinCol)
        : `${addressFromRC(absMinRow, absMinCol)}:${addressFromRC(absMaxRow, absMaxCol)}`,
    role: "unknown",
    roleConfidence: 0,
    relations: [],
  };
}

// =============================================================================
// Pairing pass
// =============================================================================

const AGGREGATION_RE =
  /\b(SUM|SUMIFS?|SUMPRODUCT|AVERAGE|AVERAGEIFS?|COUNT|COUNTIFS?|COUNTA|MIN|MAX|MEDIAN|SUBTOTAL|AGGREGATE)\s*\(/i;

/**
 * Walk components and assign structural roles based on adjacency and
 * content. The algorithm:
 *
 * 1. Identify column-direction components that are "data" (numeric / date /
 *    formula / boolean) — call these data columns.
 * 2. Find the row component (if any) directly above the data column whose
 *    span overlaps it — that's the column's header candidate.
 * 3. Find the leftmost text column-component whose row span overlaps the
 *    data column — that's the label-column candidate.
 * 4. Find a row component below the data column with an aggregation formula
 *    — that's the total row candidate.
 * 5. A row component above all data columns that is text-only with low
 *    overlap with any data column is a title/metadata candidate.
 * 6. A trailing text row component below data → footnote.
 *
 * Relations are recorded both ways (header has header-of; data column has
 * has-header), so the dialog can render either side.
 */
function pairComponents(features: RegionFeatures, components: Component[]): void {
  const byId = new Map(components.map((c) => [c.id, c]));

  // Pre-bucket
  const colData: Component[] = [];
  const colText: Component[] = [];
  const rowComponents: Component[] = [];
  for (const c of components) {
    if (c.direction === "column") {
      if (c.dominantType === "text") colText.push(c);
      else if (c.dominantType !== "boolean") colData.push(c);
    }
    if (c.direction === "row") rowComponents.push(c);
  }

  // For every column data component
  for (const col of colData) {
    // Header candidate: a row component IMMEDIATELY above (row = col.minRow - 1)
    // whose column span includes col.minCol.
    const header = rowComponents.find(
      (r) =>
        r.maxRow === col.minRow - 1 &&
        r.dominantType === "text" &&
        r.minCol <= col.minCol &&
        r.maxCol >= col.minCol
    );
    if (header) {
      addRelation(byId, col, "has-header", header.id);
      addRelation(byId, header, "header-of", col.id);
      assignRole(header, "header", 0.8);
      assignRole(col, "data-column", 0.9);
    }

    // Total candidate: a row component immediately below containing an
    // aggregation formula referencing this column.
    const total = rowComponents.find(
      (r) =>
        r.minRow === col.maxRow + 1 &&
        r.minCol <= col.minCol &&
        r.maxCol >= col.minCol &&
        rowHasAggregation(features, r)
    );
    if (total) {
      addRelation(byId, col, "has-total", total.id);
      addRelation(byId, total, "total-of", col.id);
      assignRole(total, "total-row", 0.8);
    }

    // Label-column candidate: a column-direction text component whose row
    // span overlaps `col` and whose minCol is to the LEFT of col.minCol.
    let labelCandidate: Component | null = null;
    for (const lc of colText) {
      if (lc.minCol >= col.minCol) continue;
      // Overlap on rows
      if (lc.maxRow < col.minRow || lc.minRow > col.maxRow) continue;
      // Prefer the closest (rightmost) text column to the left
      if (!labelCandidate || lc.minCol > labelCandidate.minCol) labelCandidate = lc;
    }
    if (labelCandidate) {
      addRelation(byId, col, "has-label-column", labelCandidate.id);
      addRelation(byId, labelCandidate, "label-column-of", col.id);
      assignRole(labelCandidate, "label-column", 0.7);
      // Ensure column also picks up data-column role even without header
      if (col.role === "unknown") assignRole(col, "data-column", 0.6);
    }
  }

  // Untagged row components: classify by position
  for (const row of rowComponents) {
    if (row.role !== "unknown") continue;
    const isAtTop = row.minRow === features.minRow;
    const isAtBottom = row.maxRow === features.maxRow;
    if (isAtTop && row.dominantType === "text") {
      // Above any data columns? Already not a header (otherwise it'd be set).
      // Could still be a metadata title row.
      assignRole(row, "metadata", 0.5);
    } else if (isAtBottom && row.dominantType === "text") {
      assignRole(row, "footnote", 0.5);
    }
  }

  // Untagged text column components: if standing alone (no data to right)
  // mark as label-row-like… leave 'unknown' for now to be safe.

  // Single-cell components: if at top of region and text, likely title.
  for (const single of components) {
    if (single.direction !== "single") continue;
    if (single.role !== "unknown") continue;
    if (single.dominantType !== "text") continue;
    if (single.minRow === features.minRow) {
      assignRole(single, "title", 0.6);
    } else if (single.minRow > features.minRow + 1) {
      assignRole(single, "metadata", 0.4);
    }
  }
}

function rowHasAggregation(features: RegionFeatures, row: Component): boolean {
  const rRel = row.minRow - features.minRow;
  for (let c = row.minCol - features.minCol; c <= row.maxCol - features.minCol; c++) {
    const cell = features.rows[rRel]?.cells[c];
    if (!cell) continue;
    if (cell.hasFormula && AGGREGATION_RE.test(cell.formula)) return true;
  }
  return false;
}

function assignRole(c: Component, role: ComponentRole, confidence: number): void {
  // Only assign if new confidence is higher
  if (confidence > c.roleConfidence) {
    c.role = role;
    c.roleConfidence = confidence;
  }
}

function addRelation(
  byId: Map<string, Component>,
  c: Component,
  kind: ComponentRelation["kind"],
  otherId: string
): void {
  if (!byId.has(otherId)) return;
  c.relations.push({ kind, componentId: otherId });
}
